/**
 * Single-tool run: choose a card → upload → parameters → run → progress →
 * files. Once a tool is chosen the card grid collapses into a compact
 * clickable stepper (purely presentational — the steps are peers, not a
 * sequence, hence dots instead of numbers), which frees the vertical space
 * for the actual run. Preflight runs the moment both a tool and a file are
 * chosen, so "wrong tool for this file" is a message here instead of a
 * dead job.
 *
 * Reacts to ?select= / ?job= query params on every change (not only at
 * mount), so global-search navigation works from inside this page too.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HandymanIcon from "@mui/icons-material/Handyman";
import {
  ApiError,
  api,
  type ToolEntry,
  type UploadResult,
} from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { usePageTitle } from "../hooks/usePageTitle";
import { useEditMode } from "../hooks/useCustomer";
import { resolveText } from "../lib/tooltext";
import { UploadZone } from "../components/UploadZone";
import { PreflightPanel, type PreflightState } from "../components/PreflightPanel";
import { SourceTabs } from "../components/SourceTabs";
import { ParamsForm } from "../components/ParamsForm";
import { inputRequired, missingRequired, saveStickyParams, tabsParam } from "../lib/params";
import { JobProgress } from "../components/JobProgress";
import { ValidationReportView } from "../components/ValidationReportView";
import { PageHeader } from "../components/ui/PageHeader";
import { SectionLabel } from "../components/ui/SectionLabel";
import { ErrorAlert } from "../components/ui/ErrorAlert";
import { EmptyState } from "../components/ui/EmptyState";

export function SingleToolPage() {
  const { t, i18n } = useTranslation();
  usePageTitle("tools.title");
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState<ToolEntry | null>(null);
  const [upload, setUpload] = useState<UploadResult | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [preflight, setPreflight] = useState<PreflightState>(null);

  const toolsQuery = useApiQuery(["tools"], api.tools);
  const groups = useMemo(() => toolsQuery.data ?? [], [toolsQuery.data]);
  const allTools = useMemo(() => groups.flatMap((g) => g.tools), [groups]);

  function selectTool(tool: ToolEntry | null) {
    // a different tool means a different run context: drop the old job, and
    // the file too — it was chosen as the PREVIOUS tool's input, so leaving
    // it selected made the preflight refuse it against the new tool and show
    // "this file does not match this tool" before anything had been uploaded.
    // Only on a CHANGE of tool: arriving with ?artifact= and no ?select=
    // (ArtifactFiles) preloads a file, then the operator picks the tool.
    if (selected && selected.key !== tool?.key) {
      setUpload(null);
      setPreflight(null);
    }
    setSelected(tool);
    setJobId(null);
    setFailure(null);
  }

  // ?select= — react to every change, not only the mount-time value.
  const selectParam = searchParams.get("select");
  useEffect(() => {
    if (!selectParam || groups.length === 0) return;
    const tool = groups
      .flatMap((g) => g.tools)
      .find((entry) => entry.key === selectParam);
    if (tool) {
      setSelected((current) => {
        if (current?.key === tool.key) return current;
        // same rule as selectTool: a tool CHANGE drops the previous tool's
        // file; a first selection (current null) keeps a ?artifact= preload
        if (current) {
          setUpload(null);
          setPreflight(null);
        }
        setJobId(null);
        setFailure(null);
        return tool;
      });
    }
  }, [selectParam, groups]);

  // ?job= — follow an existing run whenever the param changes.
  const jobParam = searchParams.get("job");
  useEffect(() => {
    if (jobParam) setJobId(jobParam);
  }, [jobParam]);

  // ?artifact= — start from a file that already exists on the platform (a
  // run's output, or an edited copy of one): the run becomes a FORK of the
  // run that produced it. The operator may still drop another file instead.
  const artifactParam = searchParams.get("artifact");
  const artifactName = searchParams.get("name");
  useEffect(() => {
    if (!artifactParam) return;
    setUpload({
      artifact_id: artifactParam,
      display_name: artifactName ?? artifactParam,
      byte_size: 0,
      content_sha256: "",
    });
    setJobId(null);
    setFailure(null);
  }, [artifactParam, artifactName]);

  const selectedKey = selected?.key ?? null;
  const uploadId = upload?.artifact_id ?? null;
  useEffect(() => {
    if (!selectedKey || !uploadId) {
      setPreflight(null);
      return;
    }
    let cancelled = false;
    setPreflight("checking");
    api
      .preflightTool({ tool: selectedKey, artifact_id: uploadId })
      .then((r) => {
        if (!cancelled) setPreflight({ ok: r.ok, errors: r.errors });
      })
      .catch(() => {
        if (!cancelled) setPreflight(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedKey, uploadId]);

  const missing = useMemo(
    () => (selected ? missingRequired(selected.params, values, false) : []),
    [selected, values],
  );
  const hasOverride = Object.values(overrides).some((v) => v.trim() !== "");
  // A tool may declare that, for some parameter values (a direct source
  // pull), it runs without an uploaded file.
  const needsFile = useMemo(
    () => (selected ? inputRequired(selected, values) : true),
    [selected, values],
  );
  const sourceTabs = selected ? tabsParam(selected.params) : null;
  const mismatch =
    needsFile && preflight !== null && preflight !== "checking" && !preflight.ok;
  const edit = useEditMode();
  const blocked =
    !edit.canEdit ||
    (needsFile && !upload) ||
    busy ||
    mismatch ||
    missing.length > 0 ||
    (hasOverride && !reason.trim());

  async function run() {
    if (!selected || (needsFile && !upload)) return;
    setBusy(true);
    setFailure(null);
    try {
      saveStickyParams(selected.params, values);
      const started = await api.runTool({
        tool: selected.key,
        // a file-less run sends none, even if one was uploaded earlier:
        // the tool would ignore it and the lineage must not claim it
        artifact_id: needsFile ? (upload?.artifact_id ?? null) : null,
        overrides: Object.fromEntries(
          Object.entries(overrides).filter(([, v]) => v.trim() !== ""),
        ),
        override_reason: reason,
        params: Object.fromEntries(
          Object.entries(values).filter(([, v]) => v.trim() !== ""),
        ),
      });
      setJobId(started.job_id);
    } catch (err) {
      setFailure(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <PageHeader title={t("tools.title")} subtitle={t("tools.subtitle")} />

      <Box>
        <SectionLabel>{t("tools.pick")}</SectionLabel>
        {toolsQuery.isPending && <LinearProgress sx={{ mt: 1 }} />}
        {toolsQuery.isError && (
          <ErrorAlert
            error={toolsQuery.error}
            onRetry={() => void toolsQuery.refetch()}
            sx={{ mt: 1 }}
          />
        )}
        {toolsQuery.isSuccess && groups.length === 0 && (
          <EmptyState
            icon={<HandymanIcon fontSize="large" />}
            message={t("tools.empty")}
          />
        )}
        {selected ? (
          /* A tool is chosen: the grid collapses into a clickable stepper —
             a UI shortcut between peer tools, NOT a sequence (dots, not
             numbers, so nothing suggests an order). Clicking the active
             step deselects and brings the cards back. */
          <Box sx={{ mt: 1 }}>
            <Stepper
              nonLinear
              activeStep={allTools.findIndex((tool) => tool.key === selected.key)}
              aria-label={t("tools.pick")}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              {allTools.map((tool) => {
                const active = tool.key === selected.key;
                return (
                  <Step key={tool.key} active={active} completed={false}>
                    <StepButton
                      onClick={() => selectTool(active ? null : tool)}
                      icon={
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: active ? "primary.main" : "action.disabled",
                            boxShadow: active
                              ? (theme) => `0 0 0 4px ${theme.palette.action.hover}`
                              : "none",
                          }}
                        />
                      }
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          fontWeight: active ? 700 : 400,
                          color: active ? "primary.main" : "text.secondary",
                        }}
                      >
                        {resolveText(tool.display_name, i18n.language)}
                      </Typography>
                    </StepButton>
                  </Step>
                );
              })}
            </Stepper>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              {resolveText(selected.description, i18n.language)}
            </Typography>
          </Box>
        ) : (
          groups.map((group) => (
            <Grid container spacing={2} key={group.phase} sx={{ mt: 0 }}>
              {group.tools.map((tool) => (
                <Grid item xs={12} sm={6} md={4} key={tool.key}>
                  <Card sx={{ height: "100%" }}>
                    <CardActionArea
                      sx={{ height: "100%" }}
                      onClick={() => selectTool(tool)}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="subtitle1" fontWeight={700}>
                            {resolveText(tool.display_name, i18n.language)}
                          </Typography>
                          {tool.hitl !== "NONE" && (
                            <Chip size="small" variant="outlined" label={t("tools.hitl")} />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {resolveText(tool.description, i18n.language)}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
                          {tool.key} · {t("tools.version")} {tool.version}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ))
        )}
      </Box>

      {selected && (
        <Box>
          <SectionLabel>{t("tools.upload")}</SectionLabel>
          {(selected.input_slots?.length ?? 0) > 1 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {t("tools.multiSlot")}
            </Alert>
          )}
          {sourceTabs && (
            <SourceTabs
              param={sourceTabs}
              params={selected.params}
              values={values}
              onChange={(next) => {
                setValues(next);
                setFailure(null);
                setJobId(null);
              }}
            />
          )}
          {needsFile ? (
            <>
              {upload && artifactParam === upload.artifact_id && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {t("wizard.forkFrom", { name: upload.display_name })}
                </Alert>
              )}
              <UploadZone
                // remount on a tool change: the zone keeps the uploaded file
                // in its OWN state, so without this it went on showing the
                // previous tool's file after selectTool had dropped it
                key={selected.key}
                prompt={
                  resolveText(selected.input_slots?.[0]?.label, i18n.language) ||
                  undefined
                }
                onUploaded={(result) => {
                  setUpload(result);
                  setFailure(null);
                  setJobId(null);
                }}
              />
              <PreflightPanel preflight={preflight} />
            </>
          ) : (
            <Alert severity="info">{t("tools.noFileNeeded")}</Alert>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
            {t("tools.requiredColumns")}: {selected.required_headers.join(" · ")}
          </Typography>
        </Box>
      )}

      {selected && (
        <Box>
          <SectionLabel>{t("tools.run")}</SectionLabel>
          <Card>
            <CardContent>
              {/* The action leads the card: a tool with many parameters
                  (the exceptional mapping, advanced ones unfolded) must not
                  push its own run button below the fold. */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 2 }}
              >
                {missing.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t("tools.missing", {
                      fields: missing
                        .map((p) => resolveText(p.label, i18n.language) || p.name)
                        .join(", "),
                    })}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={blocked}
                  onClick={() => void run()}
                >
                  {busy
                    ? t("tools.running")
                    : t("tools.runButton", {
                        name: resolveText(selected.display_name, i18n.language),
                      })}
                </Button>
              </Stack>

              <ParamsForm params={selected.params} values={values} onChange={setValues} />

              {selected.overrides.length > 0 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {selected.overrides.map((o) => (
                    <Grid item xs={12} sm={6} md={3} key={o.name}>
                      <TextField
                        size="small"
                        fullWidth
                        label={o.name}
                        helperText={o.description}
                        value={overrides[o.name] ?? ""}
                        onChange={(e) =>
                          setOverrides({ ...overrides, [o.name]: e.target.value })
                        }
                      />
                    </Grid>
                  ))}
                  {hasOverride && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        required
                        label={t("tools.overrideReason")}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {failure && (
        <ValidationReportView
          report={failure.body?.report}
          errors={failure.body?.errors ?? [failure.message]}
          diff={failure.body?.diff}
        />
      )}

      {jobId && (
        <Box>
          <SectionLabel>{t("tools.progress")}</SectionLabel>
          <JobProgress jobId={jobId} />
        </Box>
      )}
    </Stack>
  );
}
